import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
} from '@heroui/react';
import { Download, Eye } from 'lucide-react';
import React, { FC, useState } from 'react';

import { useHeroToast } from '../../../../hooks';
import { StockPhoto, StockUsage, useImportStockPhoto } from '../../../../queries';
import { createImageElement } from '../../model/factories';
import { useAdsGeneratorStore } from '../../model/store';

interface StockImportModalProps {
  photo: StockPhoto;
  downloadsLeft: number;
  onClose: () => void;
  /** Ustawione, gdy przeznaczenie wynika z kontekstu (wybór tła planszy). */
  lockedUsage?: StockUsage;
}

/** Rozmiar zastępczy, gdy nagłówka pliku nie udało się odczytać po stronie backendu. */
const FALLBACK_SIZE = 1000;

/**
 * Potwierdzenie pobrania. Osobny krok jest tu celowo: to jedyne miejsce w całym
 * generatorze, które zabiera pobranie z dziennej puli, więc nie może zdarzyć
 * się przypadkiem przy przeglądaniu wyników.
 */
export const StockImportModal: FC<StockImportModalProps> = ({
  photo,
  downloadsLeft,
  onClose,
  lockedUsage,
}) => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const addElement = useAdsGeneratorStore((state) => state.addElement);
  const setBackground = useAdsGeneratorStore((state) => state.setBackground);
  const { showError, showSuccess } = useHeroToast();
  const { mutateAsync: importPhoto, isPending } = useImportStockPhoto();
  const [usage, setUsage] = useState<StockUsage>(lockedUsage ?? 'element');

  if (!scene) return null;

  /** Rozmiar ramki dla podglądu wstawianego jako element planszy. */
  const previewSize = (): { width: number; height: number } => {
    const [rawWidth, rawHeight] = photo.previewSize.split('x').map(Number);
    return {
      width: Number.isFinite(rawWidth) && rawWidth > 0 ? rawWidth : FALLBACK_SIZE,
      height: Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : FALLBACK_SIZE,
    };
  };

  /**
   * Wstawia ZNAKOWANY podgląd bez pobierania pliku i bez ruszania puli.
   * Wersja robocza do ułożenia kreacji — przed publikacją trzeba to samo
   * zdjęcie pobrać, bo znak wodny zostaje na planszy i w eksporcie.
   */
  const usePreview = (): void => {
    if (usage === 'background') {
      setBackground({ kind: 'image', imageSrc: photo.previewUrl, imageStockId: photo.id });
    } else {
      const size = previewSize();
      addElement({
        ...createImageElement(photo.previewUrl, size.width, size.height, scene.width, scene.height),
        stockId: photo.id,
      });
    }

    showSuccess('Wstawiono podgląd ze znakiem wodnym. Przed publikacją pobierz zdjęcie.');
    onClose();
  };

  const confirm = async (): Promise<void> => {
    try {
      const result = await importPhoto({
        id: photo.id,
        usage,
        canvasLongEdge: Math.max(scene.width, scene.height),
      });

      const { asset } = result;

      if (usage === 'background') {
        setBackground({ kind: 'image', imageSrc: asset.url });
      } else {
        addElement(createImageElement(
          asset.url,
          asset.width || FALLBACK_SIZE,
          asset.height || FALLBACK_SIZE,
          scene.width,
          scene.height,
        ));
      }

      showSuccess(result.downloaded
        ? `Zdjęcie dodane. Zostało dzisiaj ${result.downloadsLeft} pobrań.`
        : 'Zdjęcie było już w bibliotece, pula nietknięta.');
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się pobrać zdjęcia');
    }
  };

  return (
    <Modal isOpen onClose={isPending ? undefined : onClose} size="md" radius="sm">
      <ModalContent>
        <ModalHeader>Pobrać to zdjęcie?</ModalHeader>
        <ModalBody>
          {/*
            Podgląd przycięty do proporcji planszy: widać, jak zdjęcie usiądzie
            w kadrze, zanim pobranie zabierze sztukę z dziennej puli. To wciąż
            znakowany podgląd z CDN Magnifica — licencjonowany plik pobiera się
            dopiero po potwierdzeniu i tylko on trafia na planszę.
          */}
          <div
            className="w-full overflow-hidden rounded bg-gray-100"
            style={{ aspectRatio: `${scene.width} / ${scene.height}`, maxHeight: 260 }}
          >
            <img src={photo.previewUrl} alt="" className="h-full w-full object-cover" />
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            {`Podgląd w kadrze ${scene.width} x ${scene.height} px. Znak wodny znika po pobraniu licencjonowanego pliku.`}
          </p>
          <p className="text-xs text-gray-500 leading-relaxed">
            {[photo.title, photo.author && `autor: ${photo.author}`, photo.license && `licencja: ${photo.license}`]
              .filter(Boolean)
              .join(', ')}
          </p>

          {lockedUsage ? (
            <p className="text-xs text-gray-500 leading-relaxed">
              Zdjęcie trafi na tło planszy i pobierze się w rozmiarze dopasowanym do formatu.
            </p>
          ) : (
            <RadioGroup
              size="sm"
              value={usage}
              onValueChange={(value) => setUsage(value as StockUsage)}
              label="Do czego posłuży"
            >
              <Radio value="element" description="Mniejszy plik, zdjęcie zajmuje część kadru">
                Element planszy
              </Radio>
              <Radio value="background" description="Większy plik, zdjęcie na cały format">
                Tło planszy
              </Radio>
            </RadioGroup>
          )}

          <p className="text-xs text-gray-500 leading-relaxed">
            {`Pobranie zabierze jedno z dziennych pobrań. Zostało ich dzisiaj ${downloadsLeft}. `}
            Jeśli to zdjęcie jest już w bibliotece, wstawimy je bez pobierania.
          </p>
          <p className="text-xs text-gray-500 leading-relaxed">
            Możesz też wstawić sam podgląd, bez pobrania i bez ruszania puli. Trafi na planszę
            ze znakiem wodnym, więc nadaje się na wersję roboczą, a nie do publikacji.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" radius="sm" variant="light" isDisabled={isPending} onPress={onClose}>
            Anuluj
          </Button>
          <Button
            size="sm"
            radius="sm"
            variant="flat"
            startContent={<Eye size={16} />}
            isDisabled={isPending}
            onPress={usePreview}
          >
            Wstaw podgląd
          </Button>
          <Button
            size="sm"
            radius="sm"
            className="bg-black text-white"
            startContent={<Download size={16} />}
            isLoading={isPending}
            onPress={confirm}
          >
            Pobierz i wstaw
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
